<template>
  <div class="position-manager">
    <div class="position-header">
      <el-button 
        size="small" 
        type="primary" 
        icon="el-icon-plus"
        @click="addPosition"
      >
        添加岗位
      </el-button>
    </div>
    
    <el-table
      size="small"
      :data="positionList"
      border
      style="width: 100%"
      v-loading="loading"
    >
      <el-table-column prop="name" label="岗位名称" width="150"></el-table-column>
      <el-table-column prop="code" label="岗位编码" width="120"></el-table-column>
      <el-table-column prop="description" label="岗位描述"></el-table-column>
      <el-table-column prop="sort" label="排序" width="80" align="center">
        <template slot-scope="scope">
          <el-input-number 
            v-model="scope.row.sort" 
            size="mini" 
            controls-position="right" 
            :min="0" 
            :max="999"
            @change="handleSortChange(scope.row)"
          ></el-input-number>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="80" align="center">
        <template slot-scope="scope">
          <el-tag 
            :type="scope.row.status === 1 ? 'success' : 'info'"
            size="small"
          >
            {{ scope.row.status === 1 ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column fixed="right" label="操作" width="150" align="center">
        <template slot-scope="scope">
          <el-button 
            @click="editPosition(scope.row)" 
            type="text" 
            size="small"
            icon="el-icon-edit"
          >编辑</el-button>
          <el-button 
            @click="deletePosition(scope.row)" 
            type="text" 
            size="small"
            icon="el-icon-delete"
            style="color: #f56c6c;"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 添加/编辑岗位对话框 -->
    <el-dialog
      :title="positionDialogTitle"
      :visible.sync="positionDialogVisible"
      width="500px"
      @close="handlePositionDialogClose"
    >
      <el-form 
        ref="positionFormRef" 
        :model="positionForm" 
        :rules="positionRules" 
        label-width="80px"
        size="small"
      >
        <el-form-item label="岗位名称" prop="name">
          <el-input 
            v-model="positionForm.name" 
            placeholder="请输入岗位名称"
            maxlength="50"
          ></el-input>
        </el-form-item>
        
        <el-form-item label="岗位编码" prop="code">
          <el-input 
            v-model="positionForm.code" 
            placeholder="请输入岗位编码"
            maxlength="20"
          ></el-input>
        </el-form-item>
        
        <el-form-item label="岗位描述" prop="description">
          <el-input 
            v-model="positionForm.description" 
            type="textarea"
            :rows="3"
            placeholder="请输入岗位描述"
            maxlength="200"
          ></el-input>
        </el-form-item>
        
        <el-form-item label="排序" prop="sort">
          <el-input-number 
            v-model="positionForm.sort" 
            controls-position="right" 
            :min="0" 
            :max="999"
            style="width: 120px;"
          ></el-input-number>
        </el-form-item>
        
        <el-form-item label="状态" prop="status">
          <el-switch
            v-model="positionForm.status"
            :active-value="1"
            :inactive-value="0"
            active-text="启用"
            inactive-text="停用"
          ></el-switch>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="positionDialogVisible = false" size="small">取 消</el-button>
        <el-button 
          type="primary" 
          @click="handlePositionSubmit" 
          size="small"
          :loading="positionSubmitLoading"
        >确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import AfuncApi from '@/api/afunc.js';

export default {
  name: 'PositionManager',
  props: {
    departmentId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      positionList: [],
      
      positionDialogVisible: false,
      positionDialogTitle: '添加岗位',
      positionSubmitLoading: false,
      
      positionForm: {
        ma_position_id: '',
        ma_depart_id: '',
        name: '',
        code: '',
        description: '',
        sort: 100,
        status: 1,
        is_leader: 0,
        is_hide: 0
      },
      
      positionRules: {
        name: [
          { required: true, message: '请输入岗位名称', trigger: 'blur' }
        ],
        code: [
          { required: true, message: '请输入岗位编码', trigger: 'blur' }
        ]
      }
    };
  },
  watch: {
    departmentId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadPositions();
        }
      }
    }
  },
  methods: {
    // 加载岗位数据 - 使用通用API
    async loadPositions() {
      try {
        this.loading = true;
        
        const response = await AfuncApi.call({
          model: 'ma_position',
          action: 'index',
          ma_depart_id: this.departmentId,
          page: 1,
          list_rows: 100
        });
        
        if (response.code === 1 && response.data.list) {
          this.positionList = response.data.list.data || [];
        } else {
          this.$message.error(response.msg || '获取岗位列表失败');
        }
      } catch (error) {
        console.error('获取岗位列表失败:', error);
        this.$message.error('获取岗位列表失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 添加岗位
    addPosition() {
      this.positionDialogTitle = '添加岗位';
      this.resetPositionForm();
      this.positionForm.ma_depart_id = this.departmentId;
      this.positionDialogVisible = true;
    },
    
    // 编辑岗位 - 使用通用API
    async editPosition(row) {
      try {
        this.loading = true;
        
        const response = await AfuncApi.call({
          model: 'ma_position',
          action: 'detail',
          ma_position_id: row.ma_position_id
        });
        
        if (response.code === 1 && response.data) {
          this.positionForm = {
            ma_position_id: response.data.ma_position_id,
            ma_depart_id: response.data.ma_depart_id || this.departmentId,
            name: response.data.name || '',
            code: response.data.code || '',
            description: response.data.description || '',
            sort: response.data.sort || 100,
            status: response.data.status || 1,
            is_leader: response.data.is_leader || 0,
            is_hide: response.data.is_hide || 0
          };
          
          this.positionDialogTitle = '编辑岗位';
          this.positionDialogVisible = true;
        } else {
          this.$message.error(response.msg || '获取岗位详情失败');
        }
      } catch (error) {
        console.error('获取岗位详情失败:', error);
        this.$message.error('获取岗位详情失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 删除岗位 - 使用通用API
    async deletePosition(row) {
      try {
        await this.$confirm('确定要删除该岗位吗?', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        
        this.loading = true;
        
        const response = await AfuncApi.call({
          model: 'ma_position',
          action: 'del',
          ma_position_id: row.ma_position_id
        });
        
        if (response.code === 1) {
          this.$message.success('删除成功');
          this.loadPositions();
        } else {
          this.$message.error(response.msg || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error);
          this.$message.error('删除失败');
        }
      } finally {
        this.loading = false;
      }
    },
    
    // 处理排序改变 - 使用通用API
    async handleSortChange(row) {
      try {
        const response = await AfuncApi.call({
          model: 'ma_position',
          action: 'edit',
          ma_position_id: row.ma_position_id,
          sort: row.sort
        });
        
        if (response.code === 1) {
          this.$message.success('排序更新成功');
        } else {
          this.$message.error(response.msg || '更新失败');
        }
      } catch (error) {
        console.error('更新排序失败:', error);
        this.loadPositions(); // 重新加载恢复原排序
      }
    },
    
    // 重置岗位表单
    resetPositionForm() {
      this.positionForm = {
        ma_position_id: '',
        ma_depart_id: this.departmentId,
        name: '',
        code: '',
        description: '',
        sort: 100,
        status: 1,
        is_leader: 0,
        is_hide: 0
      };
      
      if (this.$refs.positionFormRef) {
        this.$refs.positionFormRef.clearValidate();
      }
    },
    
    // 对话框关闭处理
    handlePositionDialogClose() {
      this.resetPositionForm();
    },
    
    // 提交岗位表单 - 使用通用API
    async handlePositionSubmit() {
      try {
        await this.$refs.positionFormRef.validate();
      } catch (error) {
        return;
      }
      
      try {
        this.positionSubmitLoading = true;
        
        // 准备表单数据
        const formData = {
          model: 'ma_position',
          ...this.positionForm,
          // 如果是编辑，添加更新时间
          update_time: this.positionForm.ma_position_id ? Math.floor(Date.now() / 1000) : undefined,
          // 如果是添加，添加创建时间
          create_time: this.positionForm.ma_position_id ? undefined : Math.floor(Date.now() / 1000)
        };
        
        // 决定是添加还是编辑
        const action = this.positionForm.ma_position_id ? 'edit' : 'add';
        
        const response = await AfuncApi.call({
          ...formData,
          action: action
        });
        
        if (response.code === 1) {
          this.positionDialogVisible = false;
          this.$message.success(
            this.positionForm.ma_position_id ? '修改成功' : '添加成功'
          );
          this.loadPositions();
        } else {
          this.$message.error(response.msg || '操作失败');
        }
      } catch (error) {
        console.error('操作失败:', error);
        this.$message.error('操作失败');
      } finally {
        this.positionSubmitLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.position-manager {
  padding: 20px;
  
  .position-header {
    margin-bottom: 20px;
  }
  
  .el-table {
    margin-bottom: 20px;
    border-radius: 4px;
    overflow: hidden;
    
    ::v-deep .el-table__header-wrapper {
      background: #f8f9fa;
      
      th {
        background: #f8f9fa;
        font-weight: bold;
        color: #333;
      }
    }
  }
  
  .dialog-footer {
    text-align: right;
  }
}
</style>


// WEBPACK FOOTER //
// src/views/plus/ma_depart/index/part/PositionManager.vue